import * as styles from './PartyStatusIconCssDefs';
import { FunctionComponent } from 'react';
import {
  PartyStatusIconData,
  getMappedPartyStatusIcon,
} from 'components/settings/business-defined-states/partyStatusIcons';
import clsx from 'clsx';

interface Props {
  state: PartyStatusIconData;
  isSelected?: boolean;
}

/**
 * Renders a party status's colored dot with its mapped glyph, plus a dashed
 * ring when the status's border is DASHED. Supports `isSelected` to show a
 * solid ring when the status icon is selected. Shared by the settings list and any
 * other surface that needs to display a party status icon.
 */
const PartyStatusIcon: FunctionComponent<Props> = ({ state, isSelected }) => {
  const StatusGlyph = getMappedPartyStatusIcon(state);
  const isDashed = state?.border === 'DASHED';

  return (
    <div css={styles.statusIcon}>
      <div
        className={clsx('StatusIcon-dot', state?.className, {
          'StatusIcon-dot--dashed': isDashed,
        })}
        data-testid={`party-status-dot-${state?.key}`}
        aria-hidden="true"
      >
        {isDashed && (
          <svg
            className="StatusIcon-dashedRing"
            viewBox="0 0 30 30"
            aria-hidden="true"
            data-testid={`party-status-dashed-ring-${state?.key}`}
          >
            <circle cx="15" cy="15" r="13.25" />
          </svg>
        )}
        {isSelected && (
          <svg
            className="StatusIcon-solidRing"
            viewBox="0 0 38 38"
            aria-hidden="true"
            data-testid={`party-status-solid-ring-${state?.key}`}
          >
            <circle cx="19" cy="19" r="18" className={state?.strokeColorClassName} />
          </svg>
        )}

        {StatusGlyph && (
          <span
            className="StatusIcon-glyph"
            aria-hidden="true"
            data-testid={`party-status-icon-${state?.key}`}
          >
            <StatusGlyph size={200} color="fgInverse" />
          </span>
        )}
      </div>
    </div>
  );
};

export default PartyStatusIcon;
